import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  DatePicker,
  InputNumber,
  Switch,
  Checkbox,
  Select,
  Tooltip,
  message,
  ConfigProvider,
} from 'antd';
import {
  QuestionCircleOutlined,
  CopyOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import Axios from 'axios';
import dayjs from 'dayjs';
import jaJP from 'antd/locale/ja_JP';
const { surveyCode } = require('../../Config/surveyCode');

const { TextArea } = Input;
const { RangePicker } = DatePicker;

// キャンペーンキー自動生成（8文字の英小文字+数字）
function generateCampaignKey() {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let key = '';
  for (let i = 0; i < 16; i++) {
    key += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return key;
}

// トライアルキャンペーン作成・編集ページ
function MakeTrialCampaignPage(props) {
  const [mode, setMode] = useState('new'); // 'new' or 'edit'
  const [campaignId, setCampaignId] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [campaignDescription, setCampaignDescription] = useState('');
  const [campaignKey, setCampaignKey] = useState(() => generateCampaignKey());
  const [dateRange, setDateRange] = useState([
    dayjs(),
    dayjs().add(30, 'day'),
  ]);
  const [tokenExpireDays, setTokenExpireDays] = useState(60);
  const [active, setActive] = useState(true);
  const [selectedSeasons, setSelectedSeasons] = useState([]);
  const [availableSeasons, setAvailableSeasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [surveyLocked, setSurveyLocked] = useState(false); // 登録ユーザーがいる場合サーベイ変更不可
  const [videoDeliveryToken, setVideoDeliveryToken] = useState(null); // 動画配信トークン
  const [availableDeliveries, setAvailableDeliveries] = useState([]); // 動画配信一覧

  useEffect(() => {
    // 利用可能なサーベイシーズンを取得
    fetchSeasons();
    // 動画配信一覧を取得
    fetchVideoDeliveries();

    // 編集モードの場合、キャンペーン情報を取得
    const urlParams = new URLSearchParams(props.location.search);
    const id = urlParams.get('id');
    if (id) {
      setMode('edit');
      setCampaignId(id);
      fetchCampaign(id);
    }
  }, []);

  const fetchSeasons = async () => {
    try {
      const res = await Axios.get('/api/trialCampaign/seasons');
      if (res.data.success) {
        setAvailableSeasons(res.data.seasons);
      }
    } catch (error) {
      message.error('サーベイシーズンの取得に失敗しました。');
    }
  };

  const fetchVideoDeliveries = async () => {
    try {
      const res = await Axios.get('/api/trialCampaign/video-deliveries');
      if (res.data.success) {
        setAvailableDeliveries(res.data.deliveries);
      }
    } catch (error) {
      message.error('動画配信一覧の取得に失敗しました。');
    }
  };

  const fetchCampaign = async (id) => {
    try {
      const res = await Axios.get(`/api/trialCampaign/${id}`);
      if (res.data.success) {
        const c = res.data.campaign;
        setCampaignName(c.campaign_name);
        setCampaignDescription(c.campaign_description || '');
        setCampaignKey(c.campaign_key);
        setDateRange([dayjs(c.start_date), dayjs(c.end_date)]);
        setTokenExpireDays(c.token_expire_days);
        setActive(c.active);
        setSelectedSeasons(
          c.survey_seasons
            ? c.survey_seasons.map((s) => s.survey_season_id)
            : []
        );
        setVideoDeliveryToken(c.video_delivery_token || null);
        // 登録済みユーザーがいる場合、サーベイ選択をロック
        if (res.data.hasRegisteredUsers) {
          setSurveyLocked(true);
        }
      }
    } catch (error) {
      message.error('キャンペーン情報の取得に失敗しました。');
    }
  };

  // キャンペーンキーをクリップボードにコピー
  const handleCopyKey = () => {
    navigator.clipboard.writeText(campaignKey).then(() => {
      message.success('キャンペーンキーをコピーしました。');
    }).catch(() => {
      message.error('コピーに失敗しました。');
    });
  };

  // キャンペーンキーを再生成
  const handleRegenerateKey = () => {
    setCampaignKey(generateCampaignKey());
  };

  const handleSubmit = async () => {
    // バリデーション
    if (!campaignName) {
      message.error('キャンペーン名を入力してください。');
      return;
    }
    if (!campaignKey) {
      message.error('キャンペーンキーが生成されていません。');
      return;
    }
    if (!dateRange || dateRange.length !== 2) {
      message.error('期間を設定してください。');
      return;
    }
    if (selectedSeasons.length === 0) {
      message.error('サーベイを1つ以上選択してください。');
      return;
    }

    // survey_seasons配列を構築
    const surveySeasonData = selectedSeasons.map((seasonId) => {
      const season = availableSeasons.find(
        (s) => s._id === seasonId
      );
      return {
        survey_season_id: seasonId,
        survey_code: season ? season.survey_code : '',
        survey_name: season
          ? surveyCode[season.survey_code] || season.season_name
          : '',
      };
    });

    const data = {
      campaign_name: campaignName,
      campaign_description: campaignDescription,
      // 期間は日付単位で扱うため日付のみを送る（時刻成分・ブラウザのタイムゾーンで
      // 日がずれるのを防ぐ）。サーバー側で開始日 00:00・終了日 23:59:59.999（JST）へ正規化する
      start_date: dateRange[0].format('YYYY-MM-DD'),
      end_date: dateRange[1].format('YYYY-MM-DD'),
      token_expire_days: tokenExpireDays,
      active,
      video_delivery_token: videoDeliveryToken,
    };

    // サーベイがロックされていない場合のみ送信（ロック中は変更不可のため送信しない）
    if (!surveyLocked) {
      data.survey_seasons = surveySeasonData;
    }

    setLoading(true);
    try {
      let res;
      if (mode === 'new') {
        data.campaign_key = campaignKey;
        res = await Axios.post('/api/trialCampaign/', data);
      } else {
        res = await Axios.patch(`/api/trialCampaign/${campaignId}`, data);
      }

      if (res.data.success) {
        message.success(
          mode === 'new'
            ? 'キャンペーンを作成しました。'
            : 'キャンペーンを更新しました。'
        );
        props.history.push('/trialcampaignlist');
      } else {
        message.error(res.data.message || 'エラーが発生しました。');
      }
    } catch (error) {
      const errMsg =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'エラーが発生しました。';
      message.error(errMsg);
    }
    setLoading(false);
  };

  // サーベイシーズンのチェックボックスオプション
  const seasonOptions = availableSeasons.map((s) => ({
    label: `${surveyCode[s.survey_code] || s.season_name} (${s.survey_code})`,
    value: s._id,
  }));

  return (
    <ConfigProvider locale={jaJP}>
      <div style={{ maxWidth: '700px', margin: '2rem auto', padding: '0 1rem' }}>
        <h2>
          {mode === 'new'
            ? 'トライアルキャンペーン新規作成'
            : 'トライアルキャンペーン編集'}
        </h2>

        <Form layout="vertical" style={{ marginTop: '1.5rem' }}>
          <Form.Item label="キャンペーン名" required>
            <Input
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder="例：2026年人事評価力体験キャンペーン"
            />
          </Form.Item>

          <Form.Item label="説明">
            <TextArea
              value={campaignDescription}
              onChange={(e) => setCampaignDescription(e.target.value)}
              rows={3}
              placeholder="キャンペーンの説明（任意）"
            />
          </Form.Item>

          <Form.Item
            label="キャンペーンキー"
            required
            extra="URLに使用される自動生成の識別子です。作成後は変更できません。"
          >
            <div style={{ display: 'flex', gap: '8px' }}>
              <Input
                value={campaignKey}
                disabled
                style={{ backgroundColor: '#f5f5f5', fontFamily: 'monospace' }}
              />
              {mode === 'new' && (
                <Tooltip title="キーを再生成">
                  <Button icon={<ReloadOutlined />} onClick={handleRegenerateKey} />
                </Tooltip>
              )}
              <Tooltip title="キーをコピー">
                <Button icon={<CopyOutlined />} onClick={handleCopyKey} />
              </Tooltip>
            </div>
          </Form.Item>

          <Form.Item
            label={
              <span>
                期間&nbsp;
                <Tooltip title="この期間内のみ新規トライアル登録を受け付けます。終了日は当日23:59まで有効です。期間外にURLにアクセスするとエラーが表示されます。">
                  <QuestionCircleOutlined style={{ color: 'var(--grey-45)' }} />
                </Tooltip>
              </span>
            }
            required
          >
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            label={
              <span>
                トークン有効期限（日）&nbsp;
                <Tooltip title="ユーザーが登録した日から、サーベイを実施できる日数です。例：60日の場合、登録日から60日間サーベイを受けられます。">
                  <QuestionCircleOutlined style={{ color: 'var(--grey-45)' }} />
                </Tooltip>
              </span>
            }
          >
            <InputNumber
              value={tokenExpireDays}
              onChange={(value) => setTokenExpireDays(value)}
              min={1}
              max={365}
              style={{ width: '150px' }}
            />
          </Form.Item>

          <Form.Item
            label={
              <span>
                有効/無効&nbsp;
                <Tooltip title="無効にすると、キャンペーンURLからの新規登録ができなくなります。既に登録済みのユーザーには影響しません。">
                  <QuestionCircleOutlined style={{ color: 'var(--grey-45)' }} />
                </Tooltip>
              </span>
            }
          >
            <Switch
              checked={active}
              onChange={(checked) => setActive(checked)}
              checkedChildren="有効"
              unCheckedChildren="無効"
            />
          </Form.Item>

          <Form.Item label="サーベイ選択" required>
            {surveyLocked && (
              <p style={{ color: '#faad14', fontSize: '0.85rem', marginBottom: '8px' }}>
                登録済みユーザーが存在するため、サーベイ構成は変更できません。
              </p>
            )}
            {availableSeasons.length > 0 ? (
              <Checkbox.Group
                options={seasonOptions}
                value={selectedSeasons}
                onChange={(checkedValues) => setSelectedSeasons(checkedValues)}
                disabled={surveyLocked}
                style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
              />
            ) : (
              <p style={{ color: 'var(--grey-65)' }}>
                トライアルグループに有効なサーベイシーズンがありません。
                先にサーベイシーズンを作成してください。
              </p>
            )}
          </Form.Item>

          <Form.Item
            label={
              <span>
                動画配信ページ&nbsp;
                <Tooltip title="トライアルユーザーに表示する動画配信ページを選択します。選択しない場合、動画メニューは表示されません。">
                  <QuestionCircleOutlined style={{ color: 'var(--grey-45)' }} />
                </Tooltip>
              </span>
            }
          >
            {availableDeliveries.length > 0 ? (
              <Select
                value={videoDeliveryToken}
                onChange={(value) => setVideoDeliveryToken(value)}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.children ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                placeholder="動画配信を選択（任意）"
                style={{ width: '100%' }}
              >
                {availableDeliveries.map((d) => (
                  <Select.Option key={d.token} value={d.token}>
                    {d.main_title || d.delivery_description || d.token}
                  </Select.Option>
                ))}
              </Select>
            ) : (
              <p style={{ color: 'var(--grey-65)' }}>
                動画配信がありません。先に動画納品管理で作成してください。
              </p>
            )}
          </Form.Item>

          <Form.Item style={{ marginTop: '2rem' }}>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={loading}
              style={{ marginRight: '1rem' }}
            >
              {mode === 'new' ? '作成' : '更新'}
            </Button>
            <Button onClick={() => props.history.push('/trialcampaignlist')}>
              キャンセル
            </Button>
          </Form.Item>
        </Form>
      </div>
    </ConfigProvider>
  );
}

export default MakeTrialCampaignPage;
